"use client"

import * as React from "react"
import {cn} from "@/lib/utils"
import {useI18n} from "@/lib/i18n"

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  showPasswordToggle?: boolean
  error?: boolean
  errorMessage?: string
}

// Create a context to share the RTL status
export const InputDirectionContext = React.createContext<boolean>(false)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, showPasswordToggle, error, errorMessage, ...props }, ref) => {
    // Get the current language from i18n
    const { language } = useI18n()

    // Determine if the language is RTL (currently only Persian/Farsi is RTL)
    const isRTL = language === "fa"

    // Only apply RTL/LTR direction for password fields
    const dirAttribute = type === "password" ? (isRTL ? "rtl" : "ltr") : undefined

    // Generate unique ID for error message
    const errorId = errorMessage ? `${props.id || 'input'}-error` : undefined

    return (
      <InputDirectionContext.Provider value={isRTL}>
        <input
          type={type}
          className={cn(
            "flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-base md:text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-400 placeholder:opacity-60 focus:outline-none focus:ring-0 focus:border-black focus:border-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-500 dark:placeholder:opacity-50 dark:focus:outline-none dark:focus:ring-0 dark:focus:border-white dark:focus:border-2 transition-all duration-200 min-h-[44px] touch-manipulation",
            error && "border-red-500 focus:border-red-500 dark:border-red-500 dark:focus:border-red-500",
            className,
          )}
          dir={dirAttribute}
          ref={ref}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={errorId}
          {...props}
        />
      </InputDirectionContext.Provider>
    )
  },
)
Input.displayName = "Input"

// Custom hook to access the RTL status
export function useInputDirection() {
  return React.useContext(InputDirectionContext)
}

export { Input }
